import api from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/constants';

export enum PurchaseOrderStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PARTIAL = 'partial',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface PurchaseOrderItem {
  id: number;
  item_id: number;
  item_sku: string;
  item_name: string;
  qty_ordered: number;
  qty_received: number;
  unit_price: number;
  subtotal: number;
}

export interface PurchaseOrder {
  id: number;
  po_number: string;
  supplier_id: number;
  branch_id: number;
  status: PurchaseOrderStatus;
  order_date: string;
  expected_date?: string;
  received_date?: string;
  total_amount: number;
  notes?: string;
  created_by: number;
  approved_by?: number;
  received_by?: number;
  created_at: string;
  items: PurchaseOrderItem[];
}

export interface PurchaseOrderItemCreate {
  item_id: number;
  qty_ordered: number;
  unit_price: number;
}

export interface PurchaseOrderCreate {
  supplier_id: number;
  branch_id?: number;
  expected_date?: string;
  notes?: string;
  items: PurchaseOrderItemCreate[];
}

export interface PurchaseOrderUpdate {
  expected_date?: string;
  notes?: string;
}

export interface ReceivePOItem {
  item_id: number;
  qty_received: number;
}

export interface ReceivePORequest {
  items: ReceivePOItem[];
  notes?: string;
}

const API_URL = `${API_BASE_URL}/purchase-orders`;

export const getPurchaseOrders = async (params?: {
  supplier_id?: number;
  branch_id?: number;
  status?: PurchaseOrderStatus;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}): Promise<PurchaseOrder[]> => {
  const response = await api.get<PurchaseOrder[]>(API_URL, { params });
  return response.data;
};

export const getPurchaseOrderById = async (id: number): Promise<PurchaseOrder> => {
  const response = await api.get<PurchaseOrder>(`${API_URL}/${id}`);
  return response.data;
};

export const createPurchaseOrder = async (
  po: PurchaseOrderCreate
): Promise<PurchaseOrder> => {
  const response = await api.post<PurchaseOrder>(API_URL, po);
  return response.data;
};

export const updatePurchaseOrder = async (
  id: number,
  po: PurchaseOrderUpdate
): Promise<PurchaseOrder> => {
  const response = await api.patch<PurchaseOrder>(`${API_URL}/${id}`, po);
  return response.data;
};

export const submitPurchaseOrder = async (
  id: number
): Promise<PurchaseOrder> => {
  const response = await api.post<PurchaseOrder>(`${API_URL}/${id}/send`);
  return response.data;
};

export const approvePurchaseOrder = async (
  id: number
): Promise<PurchaseOrder> => {
  // Backend doesn't have approve endpoint, use send instead
  const response = await api.post<PurchaseOrder>(`${API_URL}/${id}/send`);
  return response.data;
};

export const receivePurchaseOrder = async (
  id: number,
  data: ReceivePORequest
): Promise<PurchaseOrder> => {
  const response = await api.post<PurchaseOrder>(
    `${API_URL}/${id}/receive`,
    data
  );
  return response.data;
};

export const cancelPurchaseOrder = async (
  id: number,
  reason: string
): Promise<PurchaseOrder> => {
  const response = await api.post<PurchaseOrder>(`${API_URL}/${id}/cancel`, {
    reason,
  });
  return response.data;
};

export const forceCompletePurchaseOrder = async (
  id: number,
  reason: string
): Promise<PurchaseOrder> => {
  const response = await api.post<PurchaseOrder>(
    `${API_URL}/${id}/force-complete`,
    null,
    {
      params: { reason }
    }
  );
  return response.data;
};
